/**
 * Wills & Trusts interview. One question at a time. Answers become a checklist.
 * NextCompli does not write a will or a trust, notarize, or file with a court.
 */
import type {
  ComplianceChecklistItemDef,
  ComplianceItemPriority,
  CompliancePillarDef,
  WillsTrustsAnswers,
} from './complianceChecklists';
import { US_FORMATION_STATES } from './businessCreationInterview';

export type WillsLanguage = 'en' | 'es';
export type WillsQuestionId = 'plan' | 'state' | 'person' | 'capacity' | 'marital' | 'children' | 'counsel' | 'stage';

export type WillsChoice = {
  value: string;
  label: string;
  hint: string;
};

export type WillsQuestion = {
  id: WillsQuestionId;
  kind: 'choice' | 'text' | 'state';
  prompt: string;
  help: string;
  options?: WillsChoice[];
  placeholder?: string;
};

type Pair = readonly [string, string];

const QUESTION_ORDER: WillsQuestionId[] = [
  'plan', 'state', 'person', 'capacity', 'marital', 'children', 'counsel', 'stage',
];

/** IRS Publication 555 lists these nine as community-property states. Alaska, Tennessee, and South Dakota are elections, not this list. */
const COMMUNITY_PROPERTY_STATES = new Set(['AZ', 'CA', 'ID', 'LA', 'NV', 'NM', 'TX', 'WA', 'WI']);
const COMMUNITY_PROPERTY_ELECTION_STATES = new Set(['AK', 'TN', 'SD']);

const tx = (language: WillsLanguage, en: string, es: string): string => (language === 'es' ? es : en);
const pair = (language: WillsLanguage, value: Pair): string => (language === 'es' ? value[1] : value[0]);

const choice = (language: WillsLanguage, value: string, label: Pair, hint: Pair): WillsChoice => ({
  value,
  label: pair(language, label),
  hint: pair(language, hint),
});

export const willsStateName = (code: string | undefined): string => {
  const found = US_FORMATION_STATES.find((row) => row[0] === code);
  return found ? found[1] : (code || '');
};

const isAnswered = (id: WillsQuestionId, answers: WillsTrustsAnswers): boolean => {
  if (id === 'plan') return Boolean(answers.plan);
  if (id === 'state') return Boolean(answers.state);
  if (id === 'person') return Boolean(String(answers.person || '').trim());
  if (id === 'capacity') return Boolean(answers.capacity);
  if (id === 'marital') return Boolean(answers.marital);
  if (id === 'children') return Boolean(answers.children);
  if (id === 'counsel') return Boolean(answers.counsel);
  if (id === 'stage') return Boolean(answers.stage);
  return false;
};

export const questionApplies = (id: WillsQuestionId, answers: WillsTrustsAnswers): boolean => {
  if (answers.capacity === 'minor' && (id === 'marital' || id === 'children' || id === 'counsel' || id === 'stage')) {
    return false;
  }
  return true;
};

const presentQuestion = (id: WillsQuestionId, language: WillsLanguage): WillsQuestion => {
  switch (id) {
    case 'plan':
      return {
        id,
        kind: 'choice',
        prompt: tx(language, 'Will only, or a revocable trust plus a will?', '¿Solo un testamento, o un fideicomiso revocable más un testamento?'),
        help: tx(
          language,
          'Yes. A will can still send property into probate. The will tells the state court who should receive property that is still in the person’s name. It does not skip that court. A revocable living trust holds property during life only after the property is placed in it. A pour-over will still sends leftover property through probate and then into the trust.',
          'Sí. Un testamento aún puede enviar bienes al proceso sucesorio. El testamento le dice al tribunal del estado quién debe recibir los bienes que siguen a nombre de la persona. No se salta ese tribunal. Un fideicomiso revocable en vida tiene bienes durante la vida solo después de colocarlos en él. Un testamento residual aún envía lo que quedó fuera por el proceso sucesorio y luego al fideicomiso.',
        ),
        options: [
          choice(language, 'will', ['Will only', 'Solo testamento'], ['The checklist covers the will and the related papers. It does not add trust funding.', 'La lista cubre el testamento y los papeles relacionados. No añade el fondeo del fideicomiso.']),
          choice(language, 'trust', ['Revocable trust and pour-over will', 'Fideicomiso revocable y testamento residual'], ['The checklist adds funding steps. The trust does no work until property is placed in it.', 'La lista añade pasos de fondeo. El fideicomiso no hace nada hasta que se coloquen bienes en él.']),
        ],
      };
    case 'state':
      return {
        id,
        kind: 'state',
        prompt: tx(language, 'Which state’s law should the lawyer use?', '¿La ley de qué estado debe usar el abogado?'),
        help: tx(
          language,
          'Use the state where the person lives. Signing rules are different in every state. Do not copy Texas rules into another state.',
          'Use el estado donde vive la persona. Las reglas de firma son distintas en cada estado. No copie las reglas de Texas en otro estado.',
        ),
      };
    case 'person':
      return {
        id,
        kind: 'text',
        prompt: tx(language, 'What is the person’s legal name?', '¿Cuál es el nombre legal de la persona?'),
        help: tx(language, 'Use the name on the driver’s license or passport.', 'Use el nombre de la licencia de conducir o del pasaporte.'),
        placeholder: tx(language, 'Legal name', 'Nombre legal'),
      };
    case 'capacity':
      return {
        id,
        kind: 'choice',
        prompt: tx(language, 'Is this person 18 or older?', '¿Esta persona tiene 18 años o más?'),
        help: tx(
          language,
          'A minor does not sign their own will in this checklist. That work belongs to a lawyer.',
          'Un menor no firma su propio testamento en esta lista. Ese trabajo es del abogado.',
        ),
        options: [
          choice(language, 'adult', ['18 or older', '18 años o más'], ['The rest of the questions apply.', 'El resto de las preguntas aplica.']),
          choice(language, 'minor', ['Under 18', 'Menor de 18'], ['The checklist stops at one step: talk with a lawyer.', 'La lista se detiene en un paso: hablar con un abogado.']),
        ],
      };
    case 'marital':
      return {
        id,
        kind: 'choice',
        prompt: tx(language, 'What is this person’s marital status?', '¿Cuál es el estado civil de esta persona?'),
        help: tx(language, 'A marriage or a divorce changes who should be named. The lawyer confirms the state rule.', 'Un matrimonio o un divorcio cambia a quién se debe nombrar. El abogado confirma la regla del estado.'),
        options: [
          choice(language, 'single', ['Single', 'Soltero'], ['No spouse is added to the checklist.', 'No se añade un cónyuge a la lista.']),
          choice(language, 'married', ['Married', 'Casado'], ['The checklist asks the lawyer to address the spouse.', 'La lista pide al abogado que trate al cónyuge.']),
          choice(language, 'widowed', ['Widowed', 'Viudo'], ['The checklist asks for a review after that death.', 'La lista pide una revisión después de esa muerte.']),
          choice(language, 'divorced', ['Divorced', 'Divorciado'], ['The checklist asks the lawyer to remove an ex-spouse where the law allows.', 'La lista pide al abogado quitar a un ex cónyuge cuando la ley lo permite.']),
        ],
      };
    case 'children':
      return {
        id,
        kind: 'choice',
        prompt: tx(language, 'Does this person have children?', '¿Esta persona tiene hijos?'),
        help: tx(language, 'Minor children need a guardian named in the papers. Adult children do not.', 'Los hijos menores necesitan un tutor nombrado en los papeles. Los hijos adultos no.'),
        options: [
          choice(language, 'none', ['No children', 'Sin hijos'], ['No guardian step is added.', 'No se añade un paso de tutor.']),
          choice(language, 'minors', ['At least one child under 18', 'Al menos un hijo menor de 18'], ['The checklist adds a guardian and a backup guardian.', 'La lista añade un tutor y un tutor suplente.']),
          choice(language, 'adults', ['Children are 18 or older', 'Los hijos tienen 18 años o más'], ['The lawyer can still name them as beneficiaries. No guardian step is added.', 'El abogado aún puede nombrarlos como beneficiarios. No se añade un paso de tutor.']),
        ],
      };
    case 'counsel':
      return {
        id,
        kind: 'choice',
        prompt: tx(language, 'Does this person already have a lawyer for this plan?', '¿Esta persona ya tiene un abogado para este plan?'),
        help: tx(language, 'NextCompli does not write the will or the trust. A lawyer licensed in that state does.', 'NextCompli no redacta el testamento ni el fideicomiso. Lo hace un abogado con licencia en ese estado.'),
        options: [
          choice(language, 'retained', ['Yes, a lawyer is already hired', 'Sí, ya hay un abogado'], ['The checklist starts with confirming that lawyer.', 'La lista empieza confirmando a ese abogado.']),
          choice(language, 'looking', ['Not yet', 'Todavía no'], ['The first step is to hire a lawyer. That step blocks the rest.', 'El primer paso es contratar un abogado. Ese paso bloquea el resto.']),
        ],
      };
    default:
      return {
        id: 'stage',
        kind: 'choice',
        prompt: tx(language, 'Where are the papers today?', '¿En qué punto están los papeles hoy?'),
        help: tx(language, 'Do not mark a will signed until the witnesses and the lawyer’s review are done.', 'No marque un testamento como firmado hasta que los testigos y la revisión del abogado estén listos.'),
        options: [
          choice(language, 'none', ['Nothing is drafted yet', 'Todavía no hay borrador'], ['The lawyer prepares the papers from this checklist.', 'El abogado prepara los papeles a partir de esta lista.']),
          choice(language, 'draft', ['A draft exists and is not signed', 'Hay un borrador y no está firmado'], ['Do not sign it until the lawyer reviews it.', 'No lo firme hasta que el abogado lo revise.']),
          choice(language, 'signed', ['Already signed', 'Ya está firmado'], ['The checklist asks where the original is kept and when to read it again.', 'La lista pregunta dónde se guarda el original y cuándo volver a leerlo.']),
        ],
      };
  }
};

export const nextWillsQuestion = (
  answers: WillsTrustsAnswers,
  language: WillsLanguage = 'en',
): WillsQuestion | null => {
  const id = QUESTION_ORDER.find((questionId) => questionApplies(questionId, answers) && !isAnswered(questionId, answers));
  return id ? presentQuestion(id, language) : null;
};

export const willsQuestionNumber = (answers: WillsTrustsAnswers): number => (
  QUESTION_ORDER.filter((id) => questionApplies(id, answers) && isAnswered(id, answers)).length + 1
);

export const isWillsTrustsComplete = (answers: WillsTrustsAnswers): boolean => (
  Boolean(answers.plan) && nextWillsQuestion(answers) === null
);

export const answerWillsQuestion = (
  answers: WillsTrustsAnswers,
  id: WillsQuestionId,
  value: string,
): WillsTrustsAnswers => {
  const next: WillsTrustsAnswers = { ...answers, [id]: value.trim() };
  const index = QUESTION_ORDER.indexOf(id);
  QUESTION_ORDER.slice(index + 1).forEach((key) => {
    delete next[key];
  });
  if (!value.trim()) {
    delete next[id];
  }
  return next;
};

export const retreatWillsAnswers = (answers: WillsTrustsAnswers): WillsTrustsAnswers => {
  const answered = QUESTION_ORDER.filter((id) => questionApplies(id, answers) && isAnswered(id, answers));
  const last = answered[answered.length - 1];
  if (!last) {
    return {};
  }
  const next: WillsTrustsAnswers = { ...answers };
  const index = QUESTION_ORDER.indexOf(last);
  QUESTION_ORDER.slice(index).forEach((key) => {
    delete next[key];
  });
  return next;
};

const step = (
  language: WillsLanguage,
  spec: {
    id: string;
    title: Pair;
    action: Pair;
    hint: Pair;
    priority?: ComplianceItemPriority;
    dependsOn?: string[];
    sourceLabel?: Pair;
    sourceUrl?: string;
  },
): ComplianceChecklistItemDef => ({
  id: spec.id,
  title: pair(language, spec.title),
  action: pair(language, spec.action),
  evidenceHint: pair(language, spec.hint),
  priority: spec.priority || 'required',
  dependsOn: spec.dependsOn,
  sourceLabel: spec.sourceLabel ? pair(language, spec.sourceLabel) : undefined,
  sourceUrl: spec.sourceUrl,
  completeRequires: 'evidence',
});

export const willsTrustsPillars = (
  answers: WillsTrustsAnswers,
  language: WillsLanguage,
): CompliancePillarDef[] => {
  if (!isWillsTrustsComplete(answers)) {
    return [];
  }
  const person = String(answers.person || '').trim() || tx(language, 'this person', 'esta persona');
  const stateName = willsStateName(answers.state) || tx(language, 'that state', 'ese estado');
  const lawyer: ComplianceChecklistItemDef[] = [
    step(language, {
      id: 'wills-lawyer',
      title: answers.counsel === 'retained'
        ? ['Confirm the lawyer licensed in this state', 'Confirme al abogado con licencia en este estado']
        : ['Hire a lawyer licensed in this state', 'Contrate un abogado con licencia en este estado'],
      action: [
        `NextCompli does not write the will or the trust for ${person}. Hire or confirm a lawyer licensed in ${stateName}. The lawyer prepares and reviews every paper before anyone signs.`,
        `NextCompli no redacta el testamento ni el fideicomiso de ${person}. Contrate o confirme un abogado con licencia en ${stateName}. El abogado prepara y revisa cada papel antes de que alguien firme.`,
      ],
      hint: ['The lawyer’s name and the state of the license', 'El nombre del abogado y el estado de la licencia'],
      priority: answers.counsel === 'retained' ? 'required' : 'blocking',
    }),
  ];
  if (answers.capacity === 'minor') {
    return [{
      id: 'wills-counsel',
      label: tx(language, 'Lawyer', 'Abogado'),
      summary: tx(language, 'A person under 18 does not complete this checklist.', 'Una persona menor de 18 años no completa esta lista.'),
      items: lawyer,
    }];
  }

  const people: ComplianceChecklistItemDef[] = [
    step(language, {
      id: 'wills-executor',
      title: ['Name an executor and a backup', 'Nombre un albacea y un suplente'],
      action: [
        `Ask the lawyer to name who handles ${person}’s estate and who steps in if that person cannot.`,
        `Pida al abogado que nombre quién maneja el patrimonio de ${person} y quién entra si esa persona no puede.`,
      ],
      hint: ['Executor and backup named in the lawyer’s draft', 'Albacea y suplente nombrados en el borrador del abogado'],
      dependsOn: ['wills-lawyer'],
    }),
  ];
  if (answers.marital === 'married') {
    people.push(step(language, {
      id: 'wills-spouse',
      title: ['Tell the lawyer about the spouse', 'Informe al abogado sobre el cónyuge'],
      action: [
        'The lawyer decides how the spouse is named. Do not leave a spouse off the papers without the lawyer’s review.',
        'El abogado decide cómo se nombra al cónyuge. No deje a un cónyuge fuera de los papeles sin la revisión del abogado.',
      ],
      hint: ['Spouse addressed in the lawyer’s draft', 'Cónyuge tratado en el borrador del abogado'],
      dependsOn: ['wills-lawyer'],
    }));
  }
  if (answers.children === 'minors') {
    people.push(step(language, {
      id: 'wills-guardian',
      title: ['Name a guardian and a backup for minor children', 'Nombre un tutor y un suplente para los hijos menores'],
      action: [
        'Ask the lawyer to name who would care for children under 18, and who is the backup.',
        'Pida al abogado que nombre quién cuidaría a los hijos menores de 18 años, y quién es el suplente.',
      ],
      hint: ['Guardian and backup named in the lawyer’s draft', 'Tutor y suplente nombrados en el borrador del abogado'],
      dependsOn: ['wills-lawyer'],
    }));
  }
  if (answers.plan === 'trust') {
    people.push(step(language, {
      id: 'wills-trustee',
      title: ['Name a trustee and a successor trustee', 'Nombre un fiduciario y un fiduciario sucesor'],
      action: [
        'The person can be the first trustee of their own revocable trust. The lawyer names who takes over if they cannot serve.',
        'La persona puede ser el primer fiduciario de su propio fideicomiso revocable. El abogado nombra quién sigue si no puede servir.',
      ],
      hint: ['Trustee and successor named in the lawyer’s draft', 'Fiduciario y sucesor nombrados en el borrador del abogado'],
      dependsOn: ['wills-lawyer'],
    }));
  }

  const documents: ComplianceChecklistItemDef[] = [
    step(language, {
      id: 'wills-will',
      title: answers.plan === 'trust'
        ? ['Ask the lawyer for the pour-over will', 'Pida al abogado el testamento residual']
        : ['Ask the lawyer to prepare the will', 'Pida al abogado que prepare el testamento'],
      action: answers.plan === 'trust'
        ? [
          'The pour-over will sends property left outside the trust into the trust. The lawyer writes it. NextCompli does not.',
          'El testamento residual envía al fideicomiso los bienes que quedaron fuera. El abogado lo redacta. NextCompli no.',
        ]
        : [
          'The lawyer writes the will. NextCompli does not write it and does not file it.',
          'El abogado redacta el testamento. NextCompli no lo redacta y no lo presenta.',
        ],
      hint: ['Lawyer’s draft of the will, not signed yet', 'Borrador del testamento del abogado, aún sin firmar'],
      dependsOn: ['wills-lawyer'],
    }),
  ];
  if (answers.plan === 'trust') {
    documents.unshift(step(language, {
      id: 'wills-trust',
      title: ['Ask the lawyer for the revocable living trust', 'Pida al abogado el fideicomiso revocable en vida'],
      action: [
        'The lawyer writes the trust. An unsigned trust is not funded and does not hold property.',
        'El abogado redacta el fideicomiso. Un fideicomiso sin firmar no está fondeado y no tiene bienes.',
      ],
      hint: ['Lawyer’s draft of the trust, not signed yet', 'Borrador del fideicomiso del abogado, aún sin firmar'],
      dependsOn: ['wills-lawyer'],
    }));
  }
  if (answers.state === 'TX') {
    documents.push(step(language, {
      id: 'wills-texas-signing',
      title: ['Follow the Texas signing rules the lawyer gives you', 'Siga las reglas de firma de Texas que le dé el abogado'],
      action: [
        'Texas Estates Code chapter 251 expects a written will, the testator’s signature, and two credible witnesses who are at least 14, generally in the room together. A self-proving affidavit is sworn before a notary. The lawyer runs the signing. NextCompli does not notarize.',
        'El capítulo 251 del Código de Sucesiones de Texas espera un testamento escrito, la firma del testador y dos testigos creíbles de al menos 14 años, por lo general juntos en la sala. Una declaración jurada de autojustificación se presta ante un notario. El abogado dirige la firma. NextCompli no notariza.',
      ],
      hint: ['Signed will and, if used, the self-proving affidavit', 'Testamento firmado y, si se usa, la declaración de autojustificación'],
      dependsOn: ['wills-will'],
      sourceLabel: ['Texas Estates Code chapter 251', 'Código de Sucesiones de Texas, capítulo 251'],
      sourceUrl: 'https://statutes.capitol.texas.gov/Docs/ES/htm/ES.251.htm',
    }));
  } else {
    documents.push(step(language, {
      id: 'wills-state-signing',
      title: [`Follow the ${stateName} signing rules the lawyer gives you`, `Siga las reglas de firma de ${stateName} que le dé el abogado`],
      action: [
        `Do not copy the Texas witness rule into ${stateName}. The lawyer licensed there says who must be in the room and whether a notary is required.`,
        `No copie la regla de testigos de Texas en ${stateName}. El abogado con licencia allí dice quién debe estar en la sala y si se requiere un notario.`,
      ],
      hint: ['The lawyer’s signing instructions for this state', 'Las instrucciones de firma del abogado para este estado'],
      dependsOn: ['wills-will'],
    }));
  }
  documents.push(
    step(language, {
      id: 'wills-financial-poa',
      title: ['Ask for a financial power of attorney', 'Pida un poder financiero'],
      action: [
        'This names who can handle money if the person cannot. It is separate from the will. The lawyer prepares it.',
        'Esto nombra quién puede manejar el dinero si la persona no puede. Es aparte del testamento. El abogado lo prepara.',
      ],
      hint: ['Lawyer’s draft of the financial power of attorney', 'Borrador del poder financiero del abogado'],
      dependsOn: ['wills-lawyer'],
    }),
    step(language, {
      id: 'wills-medical-poa',
      title: ['Ask for a medical power of attorney', 'Pida un poder médico'],
      action: [
        'This names who can make health decisions if the person cannot. The lawyer prepares it.',
        'Esto nombra quién puede tomar decisiones de salud si la persona no puede. El abogado lo prepara.',
      ],
      hint: ['Lawyer’s draft of the medical power of attorney', 'Borrador del poder médico del abogado'],
      dependsOn: ['wills-lawyer'],
    }),
    step(language, {
      id: 'wills-directive',
      title: ['Ask for a directive to physicians', 'Pida una directiva para médicos'],
      action: [
        'This records treatment wishes if the person cannot speak. The lawyer prepares it. It is not the will.',
        'Esto registra los deseos de tratamiento si la persona no puede hablar. El abogado lo prepara. No es el testamento.',
      ],
      hint: ['Lawyer’s draft of the directive', 'Borrador de la directiva del abogado'],
      dependsOn: ['wills-lawyer'],
    }),
    step(language, {
      id: 'wills-health-release',
      title: ['Ask for a health-privacy release', 'Pida una autorización de privacidad de salud'],
      action: [
        'This lets the people named above receive health information. The lawyer prepares the release. NextCompli does not.',
        'Esto permite que las personas nombradas arriba reciban información de salud. El abogado prepara la autorización. NextCompli no.',
      ],
      hint: ['Lawyer’s draft of the health-privacy release', 'Borrador de la autorización de privacidad de salud del abogado'],
      dependsOn: ['wills-lawyer'],
    }),
  );
  if (answers.children === 'minors') {
    documents.push(step(language, {
      id: 'wills-guardian-declaration',
      title: ['Ask for the guardian declaration the state uses', 'Pida la declaración de tutor que usa el estado'],
      action: [
        'Some states use a separate declaration to name a guardian for minor children. The lawyer includes it when that state requires it.',
        'Algunos estados usan una declaración aparte para nombrar un tutor de hijos menores. El abogado la incluye cuando ese estado lo exige.',
      ],
      hint: ['Guardian paper in the lawyer’s packet, or the lawyer’s note that this state does not use one', 'Papel de tutor en el paquete del abogado, o la nota del abogado de que este estado no lo usa'],
      dependsOn: ['wills-lawyer'],
    }));
  }

  const signing: ComplianceChecklistItemDef[] = [
    step(language, {
      id: 'wills-do-not-sign-early',
      title: ['Do not sign before the lawyer reviews the papers', 'No firme antes de que el abogado revise los papeles'],
      action: [
        'A form from the internet is not this plan. Wait for the lawyer’s review, then sign only the way that lawyer instructs.',
        'Un formulario de internet no es este plan. Espere la revisión del abogado y luego firme solo como ese abogado indique.',
      ],
      hint: ['The lawyer’s review note before the signing date', 'La nota de revisión del abogado antes de la fecha de firma'],
      dependsOn: ['wills-will'],
      priority: 'blocking',
    }),
    step(language, {
      id: 'wills-keep-original',
      title: ['Store the original where the executor can reach it', 'Guarde el original donde el albacea pueda alcanzarlo'],
      action: [
        'Tell the executor where the signed original is. A copy in email is not the original.',
        'Diga al albacea dónde está el original firmado. Una copia en el correo no es el original.',
      ],
      hint: ['Where the original is stored, written down for the executor', 'Dónde se guarda el original, anotado para el albacea'],
      dependsOn: ['wills-will'],
    }),
  ];

  const funding: ComplianceChecklistItemDef[] = answers.plan === 'trust'
    ? [
      step(language, {
        id: 'wills-fund-trust',
        title: ['Fund the trust only the way the lawyer lists', 'Fondee el fideicomiso solo como lo liste el abogado'],
        action: [
          'A signed trust that owns nothing does not avoid probate. Retitle the accounts the lawyer lists. Do not move retirement accounts into the trust. Update the beneficiary form on those accounts instead. A deed for a home, including a homestead, goes through the lawyer.',
          'Un fideicomiso firmado que no tiene nada no evita el proceso sucesorio. Cambie el título de las cuentas que liste el abogado. No mueva cuentas de jubilación al fideicomiso. Actualice el formulario de beneficiario de esas cuentas. Una escritura de una casa, incluso una vivienda familiar, pasa por el abogado.',
        ],
        hint: ['The lawyer’s funding list, with each account marked done or left out on purpose', 'La lista de fondeo del abogado, con cada cuenta marcada como hecha o dejada fuera a propósito'],
        dependsOn: ['wills-trust'],
        priority: 'blocking',
      }),
    ]
    : [];

  const review: ComplianceChecklistItemDef[] = [
    step(language, {
      id: 'wills-review',
      title: ['Read the plan again after a life change', 'Vuelva a leer el plan después de un cambio de vida'],
      action: [
        'Read it again after a marriage, a divorce, a birth, a death, or a move to another state. The lawyer says what must be signed again.',
        'Léalo de nuevo después de un matrimonio, un divorcio, un nacimiento, una muerte o una mudanza a otro estado. El abogado dice qué hay que volver a firmar.',
      ],
      hint: ['The date of the last review', 'La fecha de la última revisión'],
    }),
  ];

  const probate: ComplianceChecklistItemDef[] = [
    step(language, {
      id: 'wills-probate-will',
      title: ['A will can still send property into probate', 'Un testamento aún puede enviar bienes al proceso sucesorio'],
      action: [
        `A will is the instruction the ${stateName} court follows. Property still titled in ${person}’s name goes through that court even after the will is signed. The will does not skip the court. NextCompli does not file the will and does not open the case.`,
        `Un testamento es la instrucción que sigue el tribunal de ${stateName}. Los bienes que siguen a nombre de ${person} pasan por ese tribunal aunque el testamento esté firmado. El testamento no se salta el tribunal. NextCompli no presenta el testamento y no abre el caso.`,
      ],
      hint: ['The lawyer’s note of what is still in this person’s name', 'La nota del abogado de lo que sigue a nombre de esta persona'],
      sourceLabel: ['American Bar Association, wills and revocable trusts', 'Colegio de Abogados de Estados Unidos, testamentos y fideicomisos revocables'],
      sourceUrl: 'https://www.americanbar.org/groups/senior_lawyers/resources/voice-of-experience/2010-2022/wills-revocable-trusts-one-size-doesnt-fit-all/',
    }),
    step(language, {
      id: 'wills-probate-still-in',
      title: ['List what can still go to probate', 'Liste lo que aún puede ir al proceso sucesorio'],
      action: [
        `These can still go through ${stateName} probate after a will is signed: an account in ${person}’s name with no pay-on-death or transfer-on-death form; land in ${person}’s name alone or shared without a right of survivorship; land in another state; life insurance, an annuity, or a retirement account payable to the estate or with no living beneficiary; a refund, a last paycheck, or a safe-deposit box in ${person}’s name. A creditor can ask the court to open the estate. The lawyer says which of these apply.`,
        `Esto aún puede pasar por el proceso sucesorio de ${stateName} después de firmar un testamento: una cuenta a nombre de ${person} sin formulario de pago o transferencia al fallecer; un terreno a nombre de ${person} solo o compartido sin derecho de supervivencia; un terreno en otro estado; un seguro de vida, una anualidad o una cuenta de jubilación pagadera al patrimonio o sin un beneficiario vivo; un reembolso, un último sueldo o una caja de seguridad a nombre de ${person}. Un acreedor puede pedir al tribunal que abra el patrimonio. El abogado dice cuáles aplican.`,
      ],
      hint: ['The lawyer’s list of property that would still go through the court', 'La lista del abogado de los bienes que aún pasarían por el tribunal'],
    }),
    step(language, {
      id: 'wills-probate-outside',
      title: ['List what often stays out of probate', 'Liste lo que a menudo queda fuera del proceso sucesorio'],
      action: [
        `These often stay out of probate, and the lawyer confirms the title: property already retitled to a funded revocable trust; a joint owner with a right of survivorship, or a tenancy by the entirety where ${stateName} uses one; a pay-on-death or transfer-on-death form; life insurance paid to a living person; and an employer retirement or life plan paid to the person named on the plan form. That form is federal ERISA law. A will does not replace it. If the form names the estate, the asset comes back into probate.`,
        `Esto a menudo queda fuera del proceso sucesorio, y el abogado confirma el título: bienes ya puestos a nombre de un fideicomiso revocable fondeado; un copropietario con derecho de supervivencia, o una tenencia por la totalidad donde ${stateName} la usa; un formulario de pago o transferencia al fallecer; un seguro de vida pagado a una persona viva; y un plan de jubilación o de vida del empleador pagado a la persona nombrada en el formulario del plan. Ese formulario es la ley federal ERISA. Un testamento no lo sustituye. Si el formulario nombra al patrimonio, el bien vuelve al proceso sucesorio.`,
      ],
      hint: ['Beneficiary forms and titles, with the estate named only where the lawyer intends that', 'Formularios de beneficiario y títulos, con el patrimonio nombrado solo donde el abogado lo quiera'],
      sourceLabel: ['Egelhoff v. Egelhoff, 532 U.S. 141 (2001)', 'Egelhoff v. Egelhoff, 532 U.S. 141 (2001)'],
      sourceUrl: 'https://www.law.cornell.edu/supremecourt/text/532/141',
    }),
    step(language, {
      id: 'wills-probate-ancillary',
      title: ['Ask about land in another state', 'Pregunte por un terreno en otro estado'],
      action: [
        `Land outside ${stateName} can need its own court case where the land sits, even after ${stateName} accepts the will. The lawyer says whether that second case is required.`,
        `Un terreno fuera de ${stateName} puede necesitar su propio caso judicial donde está el terreno, aunque ${stateName} acepte el testamento. El abogado dice si ese segundo caso hace falta.`,
      ],
      hint: ['Each state where this person owns land, and the lawyer’s note on a second case', 'Cada estado donde esta persona tiene terreno, y la nota del abogado sobre un segundo caso'],
    }),
    step(language, {
      id: 'wills-probate-small',
      title: [`Ask the lawyer to read the current ${stateName} probate statute`, `Pida al abogado que lea la ley sucesoria vigente de ${stateName}`],
      action: [
        `${stateName} may allow a short court paper when the estate is small or when a will is used only to clear title. The dollar limit and the name of that paper are in the current ${stateName} statute. This checklist does not print a dollar amount. The number changes, and it is not the same in every state. Do not copy another state’s limit or witness rule into ${stateName}.`,
        `${stateName} puede permitir un escrito corto cuando el patrimonio es pequeño o cuando un testamento solo sirve para aclarar el título. El límite en dólares y el nombre de ese escrito están en la ley vigente de ${stateName}. Esta lista no imprime un monto. El número cambia y no es igual en cada estado. No copie el límite ni la regla de testigos de otro estado en ${stateName}.`,
      ],
      hint: [`The lawyer’s cite to the current ${stateName} statute, including any dollar limit`, `La cita del abogado a la ley vigente de ${stateName}, incluido cualquier límite en dólares`],
    }),
    step(language, {
      id: 'wills-probate-holograph',
      title: ['Do not use a handwritten will with no witnesses', 'No use un testamento manuscrito sin testigos'],
      action: [
        `This checklist does not use a handwritten will that has no witnesses, even if ${stateName} allows one. The lawyer prepares the will and runs the signing.`,
        `Esta lista no usa un testamento manuscrito sin testigos, aunque ${stateName} lo permita. El abogado prepara el testamento y dirige la firma.`,
      ],
      hint: ['The lawyer’s witnessed will, not a handwritten page', 'El testamento con testigos del abogado, no una hoja manuscrita'],
    }),
    step(language, {
      id: 'wills-federal-tax',
      title: ['Separate federal estate tax from probate', 'Separe el impuesto federal al patrimonio del proceso sucesorio'],
      action: [
        `There is no federal probate court. Federal estate tax is a separate question. For a death in 2026, the basic exclusion amount is $15,000,000. The executor uses IRS Form 706 when the gross estate plus adjusted taxable gifts is over that amount, or when a surviving spouse wants the unused exclusion. A revocable trust that skips probate is still in the gross estate when ${person} could revoke it. Life insurance ${person} owned can be in the gross estate too. A will does not decide whether Form 706 is due. NextCompli does not file it.`,
        `No hay un tribunal sucesorio federal. El impuesto federal al patrimonio es otra pregunta. Para una muerte en 2026, la exclusión básica es de $15,000,000. El albacea usa el Formulario 706 del IRS cuando el patrimonio bruto más las donaciones sujetas a impuesto supera ese monto, o cuando el cónyuge sobreviviente quiere la exclusión no usada. Un fideicomiso revocable que evita el proceso sucesorio sigue en el patrimonio bruto si ${person} podía revocarlo. El seguro de vida que ${person} era dueño también puede estar en el patrimonio bruto. Un testamento no decide si el Formulario 706 hace falta. NextCompli no lo presenta.`,
      ],
      hint: ['The lawyer or tax preparer’s note on whether Form 706 is required', 'La nota del abogado o del preparador de impuestos sobre si el Formulario 706 hace falta'],
      sourceLabel: ['IRS, estate and gift tax for 2026', 'IRS, impuesto al patrimonio y a las donaciones para 2026'],
      sourceUrl: 'https://www.irs.gov/businesses/small-businesses-self-employed/whats-new-estate-and-gift-tax',
    }),
  ];
  if (answers.plan === 'trust') {
    probate.splice(3, 0, step(language, {
      id: 'wills-probate-pour',
      title: ['A pour-over will still uses probate', 'Un testamento residual aún usa el proceso sucesorio'],
      action: [
        `The pour-over will does not skip probate. Anything still outside the trust is probated in ${stateName}, and the court then sends what is left into the trust. Do not use the pour-over will as the way to fund the trust.`,
        `El testamento residual no evita el proceso sucesorio. Lo que siga fuera del fideicomiso se tramita en ${stateName}, y el tribunal luego envía lo que queda al fideicomiso. No use el testamento residual como la forma de fondear el fideicomiso.`,
      ],
      hint: ['The lawyer’s funding list, separate from the pour-over will', 'La lista de fondeo del abogado, aparte del testamento residual'],
      sourceLabel: ['Florida Bar, the revocable trust', 'Colegio de Abogados de Florida, el fideicomiso revocable'],
      sourceUrl: 'https://www.floridabar.org/public/consumer/pamphlet028/',
    }));
  }
  if (answers.marital === 'married' && COMMUNITY_PROPERTY_STATES.has(String(answers.state || ''))) {
    probate.push(step(language, {
      id: 'wills-community',
      title: ['Ask the lawyer about the spouse’s community-property share', 'Pregunte al abogado por la parte de bienes gananciales del cónyuge'],
      action: [
        `${stateName} is one of the nine community-property states in IRS Publication 555. Property acquired during the marriage while living there can belong half to the spouse. The will does not erase that half. The lawyer addresses it.`,
        `${stateName} es uno de los nueve estados de bienes gananciales de la Publicación 555 del IRS. Los bienes adquiridos durante el matrimonio mientras se vive allí pueden pertenecer por mitad al cónyuge. El testamento no borra esa mitad. El abogado lo trata.`,
      ],
      hint: ['The lawyer’s note on the spouse’s community-property share', 'La nota del abogado sobre la parte de bienes gananciales del cónyuge'],
      sourceLabel: ['IRS Publication 555', 'Publicación 555 del IRS'],
      sourceUrl: 'https://www.irs.gov/publications/p555',
    }));
  }
  if (answers.marital === 'married' && COMMUNITY_PROPERTY_ELECTION_STATES.has(String(answers.state || ''))) {
    probate.push(step(language, {
      id: 'wills-community-election',
      title: ['Ask whether a community-property election was made', 'Pregunte si se hizo una elección de bienes gananciales'],
      action: [
        `${stateName} lets some couples elect community property. That election is not automatic. IRS Publication 555 does not treat ${stateName} as one of the nine community-property states. The lawyer says whether an election was made and what it changes.`,
        `${stateName} permite que algunas parejas elijan bienes gananciales. Esa elección no es automática. La Publicación 555 del IRS no trata a ${stateName} como uno de los nueve estados de bienes gananciales. El abogado dice si hubo una elección y qué cambia.`,
      ],
      hint: ['The lawyer’s note on whether the election exists', 'La nota del abogado sobre si existe la elección'],
      sourceLabel: ['IRS Publication 555', 'Publicación 555 del IRS'],
      sourceUrl: 'https://www.irs.gov/publications/p555',
    }));
  }
  if (answers.state === 'TX') {
    probate.push(step(language, {
      id: 'wills-texas-muniment',
      title: ['Treat a Texas muniment of title as probate', 'Trate un muniment of title de Texas como proceso sucesorio'],
      action: [
        'A Texas court can admit a will to probate as a muniment of title when the estate owes no unpaid debt other than a debt secured by real estate, or when the estate does not need administration. That filing is still probate. It is not a shortcut for another state. The lawyer files it. NextCompli does not.',
        'Un tribunal de Texas puede admitir un testamento al proceso sucesorio como muniment of title cuando el patrimonio no debe una deuda impaga distinta de una deuda garantizada con bienes raíces, o cuando el patrimonio no necesita administración. Esa presentación sigue siendo un proceso sucesorio. No es un atajo para otro estado. El abogado la presenta. NextCompli no.',
      ],
      hint: ['The lawyer’s note on whether a muniment of title fits, or why it does not', 'La nota del abogado sobre si un muniment of title cabe, o por qué no'],
      sourceLabel: ['Texas Estates Code chapter 257', 'Código de Sucesiones de Texas, capítulo 257'],
      sourceUrl: 'https://statutes.capitol.texas.gov/Docs/ES/htm/ES.257.htm',
    }));
  }

  const pillars: CompliancePillarDef[] = [
    {
      id: 'wills-people',
      label: tx(language, 'People', 'Personas'),
      summary: tx(language, 'Who the lawyer names in the papers.', 'A quién nombra el abogado en los papeles.'),
      items: people,
    },
    {
      id: 'wills-documents',
      label: tx(language, 'Papers', 'Papeles'),
      summary: tx(language, 'What the lawyer prepares. NextCompli does not write these.', 'Lo que prepara el abogado. NextCompli no lo redacta.'),
      items: documents,
    },
    {
      id: 'wills-signing',
      label: tx(language, 'Signing', 'Firma'),
      summary: tx(language, 'Sign only after the lawyer reviews the papers.', 'Firme solo después de que el abogado revise los papeles.'),
      items: signing,
    },
  ];
  if (funding.length) {
    pillars.push({
      id: 'wills-funding',
      label: tx(language, 'Funding', 'Fondeo'),
      summary: tx(language, 'A trust works only after property is placed in it.', 'Un fideicomiso funciona solo después de colocar bienes en él.'),
      items: funding,
    });
  }
  pillars.push({
    id: 'wills-review',
    label: tx(language, 'Review', 'Revisión'),
    summary: tx(language, 'Read the plan again when life changes.', 'Vuelva a leer el plan cuando cambie la vida.'),
    items: review,
  });
  pillars.unshift({
    id: 'wills-probate',
    label: tx(language, 'Probate', 'Proceso sucesorio'),
    summary: tx(
      language,
      'A signed will can still send property through the state court. Federal estate tax is a separate question.',
      'Un testamento firmado aún puede enviar bienes por el tribunal del estado. El impuesto federal al patrimonio es otra pregunta.',
    ),
    items: probate,
  });
  pillars.unshift({
    id: 'wills-counsel',
    label: tx(language, 'Lawyer', 'Abogado'),
    summary: tx(language, 'A lawyer licensed in this state prepares the papers.', 'Un abogado con licencia en este estado prepara los papeles.'),
    items: lawyer,
  });
  return pillars;
};

export const willsTrustsAnswerLines = (answers: WillsTrustsAnswers, language: WillsLanguage): string[] => {
  const lines: string[] = [];
  if (answers.plan === 'will') lines.push(tx(language, 'Plan: will only', 'Plan: solo testamento'));
  if (answers.plan === 'trust') lines.push(tx(language, 'Plan: revocable trust and pour-over will', 'Plan: fideicomiso revocable y testamento residual'));
  if (answers.state) lines.push(`${tx(language, 'State', 'Estado')}: ${willsStateName(answers.state)}`);
  if (answers.person) lines.push(`${tx(language, 'Person', 'Persona')}: ${answers.person}`);
  if (answers.capacity === 'adult') lines.push(tx(language, 'Age: 18 or older', 'Edad: 18 años o más'));
  if (answers.capacity === 'minor') lines.push(tx(language, 'Age: under 18', 'Edad: menor de 18'));
  if (answers.marital) lines.push(`${tx(language, 'Marital status', 'Estado civil')}: ${answers.marital}`);
  if (answers.children === 'minors') lines.push(tx(language, 'Children: at least one under 18', 'Hijos: al menos uno menor de 18'));
  if (answers.children === 'adults') lines.push(tx(language, 'Children: 18 or older', 'Hijos: 18 años o más'));
  if (answers.children === 'none') lines.push(tx(language, 'Children: none', 'Hijos: ninguno'));
  if (answers.counsel === 'retained') lines.push(tx(language, 'Lawyer: already hired', 'Abogado: ya contratado'));
  if (answers.counsel === 'looking') lines.push(tx(language, 'Lawyer: not hired yet', 'Abogado: aún no contratado'));
  if (answers.stage === 'none') lines.push(tx(language, 'Papers: not drafted', 'Papeles: sin borrador'));
  if (answers.stage === 'draft') lines.push(tx(language, 'Papers: draft, not signed', 'Papeles: borrador, sin firmar'));
  if (answers.stage === 'signed') lines.push(tx(language, 'Papers: already signed', 'Papeles: ya firmados'));
  return lines;
};
